var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { internalAnalytics } from "./analytics/index.mjs";
import { trackABTestAssigned } from "./analytics/events.mjs";
function includeInABTag(config, testId) {
  if (!(config == null ? void 0 : config[testId])) {
    return false;
  }
  if (isPlaceboTest(testId)) {
    return false;
  }
  const { active, excludeFromABTag } = config[testId];
  return active && !excludeFromABTag;
}
function validateConfig(config) {
  const abTagLength = Object.keys(config).filter((testId) => includeInABTag(config, testId)).map(
    (testId) => (
      // 4 symbols from the start of the test ID
      Math.min(4, testId.length) + // 2 separators per test: `-` between the id and bucket, `,` between tests
      // (off-by-one on the last test)
      2 + // buckets are recodrded as indexes, so take the string length of the highest digit
      Math.max(config[testId].buckets.length - 1, 0).toString().length
    )
  ).reduce(
    (a, b) => a + b,
    // fix the off-by-one for the last separator
    -1
  );
  const abTagAllowedLength = 1024;
  if (abTagLength > abTagAllowedLength) {
    console.warn(
      `AB tag can become too long. Try to limit the number of tests to 140 to prevent this from happening`
    );
  }
}
const defaultPlaceboTests = {
  "0000_placebo_2": {
    active: true,
    default: "a",
    buckets: [{ id: "a" }, { id: "b" }]
  },
  "0000_placebo_3": {
    active: true,
    default: "a",
    buckets: [{ id: "a" }, { id: "b" }, { id: "c" }]
  },
  "0000_placebo_4": {
    active: true,
    default: "a",
    buckets: [{ id: "a" }, { id: "b" }, { id: "c" }, { id: "d" }]
  },
  "0000_placebo_5": {
    active: true,
    default: "a",
    buckets: [{ id: "a" }, { id: "b" }, { id: "c" }, { id: "d" }, { id: "e" }]
  }
};
class ABTests {
  constructor(userID, appName, platformStorage, getExternalABTag) {
    __publicField(this, "config");
    __publicField(this, "hash");
    __publicField(this, "isNewPlayer");
    __publicField(this, "storageKey");
    __publicField(this, "storage");
    __publicField(this, "tests");
    this.userID = userID;
    this.platformStorage = platformStorage;
    this.getExternalABTag = getExternalABTag;
    this.storageKey = appName + "AB";
    this.isNewPlayer = platformStorage.entry.count === 1;
    this.storage = {
      tests: {},
      userID: this.userID
    };
  }
  /**
   * Initialize A/B tests.
   *
   * @param config tests configuration
   * @param hash generate a function used for random bucket assignment
   * @param storageKey optionally override the default localStorage key derived from `appName`.
   *
   * @deprecated Use [Replicant A/B tests](https://docs.dev.gc-internal.net/replicant/ab-tests/) instead.
   */
  initialize(config, hash, storageKey) {
    console.warn(
      "GCInstant A/B tests are deprecated. Please use Replicant A/B tests instead: https://docs.dev.gc-internal.net/replicant/ab-tests/."
    );
    validateConfig(config);
    this.config = { ...defaultPlaceboTests, ...config };
    this.hash = hash;
    if (storageKey) {
      this.storageKey = storageKey;
    }
    this.load();
    this.initializeTests();
    this.save();
    this.applyUserProperties();
  }
  /**
   * Load test assignments from platform storage, falling back to local storage.
   */
  load() {
    if (Object.keys(this.platformStorage.abTests || {}).length > 0) {
      this.storage.tests = { ...this.platformStorage.abTests };
      return;
    }
    try {
      const data = localStorage.getItem(this.storageKey);
      if (data) {
        this.storage = JSON.parse(data);
        const userID = this.storage.userID;
        if (userID === void 0) {
          this.storage.userID = this.userID;
        } else if (userID !== this.userID) {
          this.storage.tests = {};
          this.storage.userID = this.userID;
        }
      }
    } catch (e) {
      console.error("FAILED TO LOAD AB TESTS!", e);
    }
  }
  /**
   * Store test assignments to platform storage.
   */
  save() {
    this.platformStorage.assign(
      (storage) => storage.abTests = { ...this.storage.tests }
    );
  }
  /**
   * Initialize all active tests and set assignments if necessary
   */
  initializeTests() {
    this.tests = this.storage.tests;
    for (const testID in this.config) {
      const testConfig = this.config[testID];
      if (!testConfig.active)
        continue;
      if (testConfig.assignManually)
        continue;
      if (this.tests[testID])
        continue;
      if (!this.isNewPlayer && testConfig.newPlayersOnly)
        continue;
      const bucketID = this.getUserBucketID(testID, this.userID);
      const newPlayersOnly = testConfig.newPlayersOnly || false;
      this.assignTest(testID, bucketID, newPlayersOnly);
    }
  }
  /**
   * Assign a user in a bucket for a specific test
   */
  assignTest(testID, bucketID, newPlayersOnly) {
    this.tests[testID] = bucketID;
    trackABTestAssigned({
      test: testID,
      bucket: bucketID,
      newPlayersOnly
    });
  }
  /**
   * Force bucket assignment for a specific test
   */
  assignTestManually(testID, forceBucketID, forceAssign) {
    this.throwIfNotInitialized();
    const testConfig = this.config[testID];
    const validBucketIds = testConfig.buckets.map(({ id }) => id);
    if (forceBucketID !== void 0 && !validBucketIds.includes(forceBucketID)) {
      throw new Error(
        `Invalid bucket id '${forceBucketID}' for test '${testID}'. Valid bucket ids are: [${validBucketIds.join(", ")}]`
      );
    }
    if (this.tests[testID] && !forceAssign || !testConfig || !testConfig.active) {
      return;
    }
    this.assignTest(
      testID,
      forceBucketID || this.getUserBucketID(testID, this.userID),
      false
    );
    this.save();
    this.applyUserProperties();
  }
  /**
   * Store test assignment to analytics properties
   */
  applyUserProperties() {
    const internalABTag = this.getAbTag();
    const externalABTag = this.getExternalABTag();
    const props = {
      ABtag: internalABTag && externalABTag ? `${internalABTag},${externalABTag}` : internalABTag || externalABTag
    };
    for (const testID in this.tests) {
      props["abTest_" + testID] = this.tests[testID];
    }
    internalAnalytics.setUserProperties(props, internalAnalytics.toDefault);
  }
  /**
   * Add player's A/B bucket assignments into `payload` as `sourceAB_*`-prefixed properties.
   *
   * @param payload Analytics payload object
   */
  applyPayloadProperties(payload) {
    this.throwIfNotInitialized();
    for (const testID in this.tests) {
      const test = this.config[testID];
      if (test && test.active) {
        payload["sourceAB_" + testID] = this.tests[testID];
      }
    }
    return payload;
  }
  getAssignedNonPlaceboTestIds() {
    if (!this.config) {
      return [];
    }
    return this.getListOfActiveTests().map((test) => test.id).filter((testId) => !isPlaceboTest(testId));
  }
  getListOfActiveTests() {
    this.throwIfNotInitialized();
    const activeTests = [];
    for (const testID in this.config) {
      const test = this.config[testID];
      if (test.active) {
        activeTests.push({ ...test, id: testID });
      }
    }
    return activeTests;
  }
  getBucketID(testID) {
    this.throwIfNotInitialized();
    return this.tests[testID];
  }
  getUserBucketID(testID, userID) {
    this.throwIfNotInitialized();
    const buckets = this.config[testID].buckets;
    const hash = this.hash(testID + userID);
    const roll = Math.min(
      buckets.length - 1,
      Math.floor(hash() * buckets.length)
    );
    return buckets[roll].id;
  }
  getBucketConfig(testID) {
    this.throwIfNotInitialized();
    const testConfig = this.config[testID];
    if (!testConfig) {
      console.error("Invalid AB test name: " + testID);
      return null;
    }
    let bucketID = this.tests[testID];
    if (!bucketID) {
      bucketID = testConfig.default || "control";
    }
    const buckets = testConfig.buckets;
    for (const bucket of buckets) {
      if (bucket.id === bucketID) {
        return bucket;
      }
    }
    console.error("Invalid AB config for " + testID + "/" + bucketID);
    return null;
  }
  getAbTag() {
    return (
      // an the AB tag contains active tests
      Object.keys(this.tests).filter((testId) => includeInABTag(this.config, testId)).sort().map((testId) => this.getTestTag(testId)).join(",")
    );
  }
  throwIfNotInitialized() {
    if (!this.config) {
      throw Error("Initialize ABTests before usage!");
    }
  }
  getTestTag(testId) {
    const bucketId = this.getBucketID(testId);
    const bucketIndex = this.config[testId].buckets.findIndex(
      (bucket) => bucket.id === bucketId
    );
    return `${testId.substring(0, 4)}-${bucketIndex}`;
  }
}
function isPlaceboTest(testId) {
  return testId.startsWith("0000_placebo");
}
export {
  ABTests,
  ABTests as default,
  isPlaceboTest
};
